import React, { useState, useEffect } from 'react';
import { Clock, MapPin, Phone, Droplet, Check, Calendar, Upload, X, Camera, Filter, TrendingUp, Heart, Users, AlertCircle, CheckCircle, Clock as ClockIcon, AlertTriangle, PhoneCall } from 'lucide-react';
import { Hospital } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../../config/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

const AcceptedRequests = () => {
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const { token } = useAuth();
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [donationPhoto, setDonationPhoto] = useState(null);
  const [donationNotes, setDonationNotes] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [submittingConfirmation, setSubmittingConfirmation] = useState(false);

  useEffect(() => {
    fetchAcceptedRequests();
  }, []);

  const fetchAcceptedRequests = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.get('/requests/accepted');
      console.log('Fetched accepted requests:', response.data);

      // Log the status values for debugging
      console.log('Status values in response:', response.data.map(req => req.status));

      const processedRequests = response.data.map(request => {
        const createdDate = request.createdAt ? new Date(request.createdAt) : new Date();
        const formattedCreatedDate = createdDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });

        const formattedCreatedTime = createdDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });

        const acceptedDate = request.acceptedDate ? new Date(request.acceptedDate) : new Date();
        const formattedAcceptedDate = acceptedDate.toLocaleDateString('en-IN', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });

        const formattedAcceptedTime = acceptedDate.toLocaleTimeString('en-IN', {
          hour: '2-digit',
          minute: '2-digit'
        });

        return {
          ...request,
          formattedCreatedDate,
          formattedCreatedTime,
          formattedAcceptedDate,
          formattedAcceptedTime
        };
      });

      // Filter out requests with 'donated' status
      const filteredRequests = processedRequests.filter(req =>
        req.status !== 'donated' && req.status !== 'Donated'
      );

      const sortedRequests = filteredRequests.sort((a, b) => {
        const dateA = new Date(a.acceptedDate || a.createdAt || 0);
        const dateB = new Date(b.acceptedDate || b.createdAt || 0);
        return dateB - dateA;
      });

      setAcceptedRequests(sortedRequests);
    } catch (error) {
      console.error('Error fetching accepted requests:', error);
      setError('Failed to load your accepted requests. Please try again later.');
      toast.error('Failed to load your accepted requests');
    } finally {
      setLoading(false);
    }
  };

  const filteredRequests = acceptedRequests.filter(req => {
    // Status filter
    let statusMatch = true;
    if (statusFilter) {
      if (statusFilter === 'rejected') {
        statusMatch = req.status === 'rejected' || req.status === 'Rejected';
      } else if (statusFilter === 'pending_confirmation') {
        statusMatch = req.status === 'pending_confirmation' ||
          req.status === 'Pending_confirmation' ||
          req.status === 'Pending Confirmation';
      } else {
        statusMatch = req.status === statusFilter;
      }
    }

    // Date filter - check if the request was accepted on the selected date
    let dateMatch = true;
    if (selectedDate && req.acceptedDate) {
      const acceptedDate = new Date(req.acceptedDate);
      const selectedDateObj = new Date(selectedDate);

      // Compare only the date part (year, month, day)
      const acceptedDateStr = acceptedDate.toISOString().split('T')[0];
      const selectedDateStr = selectedDateObj.toISOString().split('T')[0];

      dateMatch = acceptedDateStr === selectedDateStr;
    }

    return statusMatch && dateMatch;
  });

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const setToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  const clearDateFilter = () => {
    setSelectedDate('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateWithTime = (dateString) => {
    if (!dateString) return 'Not available';

    try {
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Not available';
      }

      const formattedDate = date.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });

      let hours = date.getHours();
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes} ${ampm}`;

      return `${formattedDate} at ${formattedTime}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Not available';
    }
  };

  const getStatusBadgeColor = (status, request) => {
    switch (status) {
      case 'accepted':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'donated':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'pending_confirmation':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'pending':
        return request && request.needsReupload ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-blue-50 text-blue-700 border-blue-200';
      case 'rejected':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'cancelled':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusDisplayText = (status) => {
    switch (status) {
      case 'pending_confirmation':
        return 'Pending Confirmation';
      case 'pending':
        return request => request.needsReupload ? 'Reupload Required' : 'Pending';
      case 'rejected':
        return 'Rejected';
      case 'donated':
        return 'Donated';
      default:
        return status;
    }
  };

  const getStatusIcon = (status, request) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'donated':
        return <Heart className="w-4 h-4" />;
      case 'pending_confirmation':
        return <ClockIcon className="w-4 h-4" />;
      case 'pending':
        return request && request.needsReupload ? <AlertTriangle className="w-4 h-4" /> : <ClockIcon className="w-4 h-4" />;
      case 'rejected':
        return <X className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const openConfirmationModal = (request) => {
    setSelectedRequest(request);
    setDonationPhoto(null);
    setDonationNotes('');
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
    setSelectedRequest(null);
    setDonationPhoto(null);
    setDonationNotes('');
  };

  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Photo size must be less than 5MB');
        return;
      }

      if (!file.type.match('image.*')) {
        toast.error('Only image files are allowed');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setDonationPhoto({
          file: file,
          preview: event.target.result
        });
        toast.success('Image uploaded successfully! Click "Confirm Donation" to submit.', {
          icon: '📸',
          duration: 4000
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async (file) => {
    setUploadingPhoto(true);

    try {
      toast.loading('Uploading your donation proof...', {
        id: 'upload-toast'
      });

      const formData = new FormData();
      formData.append('photo', file);

      const response = await api.post('/upload', formData);

      toast.success('Photo uploaded successfully!', {
        id: 'upload-toast'
      });

      return response.data.url;
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload donation photo. Please try again.', {
        id: 'upload-toast'
      });
      throw new Error('Failed to upload donation photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const submitDonationConfirmation = async () => {
    if (!donationPhoto) {
      toast.error('Please upload a photo as proof of donation');
      return;
    }

    setSubmittingConfirmation(true);

    try {
      toast.loading('Submitting your donation confirmation...', {
        id: 'confirmation-toast'
      });

      const requestId = selectedRequest.requestId || selectedRequest.request || selectedRequest._id;

      if (!requestId) {
        throw new Error('Request ID not found');
      }

      const formData = new FormData();
      formData.append('donationProof', donationPhoto.file);
      formData.append('notes', donationNotes);

      const response = await api.post(`/requests/${requestId}/confirm-donation`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Donation confirmation response:', response.data);

      // Update the local state first
      setAcceptedRequests(prev =>
        prev.map(req => {
          const currentReqId = req.requestId || req.request || req._id;
          const selectedReqId = selectedRequest.requestId || selectedRequest.request || selectedRequest._id;

          return currentReqId === selectedReqId
            ? {
              ...req,
              status: 'pending_confirmation',
              donationDate: new Date().toISOString(),
              donationProofPhoto: response.data.photoUrl || response.data.donationProofPhoto,
              notes: donationNotes
            }
            : req;
        })
      );

      // Make sure to close the modal before showing the success toast
      setShowConfirmationModal(false);
      setSelectedRequest(null);
      setDonationPhoto(null);
      setDonationNotes('');

      // Show a more detailed success notification
      toast.success(
        <div>
          <p className="font-medium">Donation confirmation submitted successfully!</p>
          <p className="text-sm mt-1">Thank you for your contribution. The requester will verify your donation soon.</p>
        </div>,
        {
          id: 'confirmation-toast',
          duration: 5000,
          icon: '🎉'
        }
      );

      // Fetch updated data after a short delay to ensure the modal is closed
      setTimeout(() => {
        fetchAcceptedRequests();
      }, 300);
    } catch (error) {
      console.error('Error confirming donation:', error);
      toast.error('Failed to confirm donation. Please try again.', {
        id: 'confirmation-toast'
      });
    } finally {
      setSubmittingConfirmation(false);
    }
  };

  // Handle phone call
  const handleCallPatient = (phoneNumber, patientName) => {
    if (!phoneNumber) {
      toast.error('Phone number not available for this patient');
      return;
    }

    // Format phone number for calling
    const formattedPhone = phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');

    // Check if it's a valid phone number
    if (formattedPhone.length < 10) {
      toast.error('Invalid phone number format');
      return;
    }

    // Show confirmation dialog
    if (window.confirm(`Call ${patientName} at ${phoneNumber}?`)) {
      // Use tel: protocol to initiate call
      window.location.href = `tel:${formattedPhone}`;

      // Show success message
      toast.success(`Initiating call to ${patientName}...`);
    }
  };

  // Calculate statistics
  const totalRequests = filteredRequests.length;
  const pendingConfirmation = filteredRequests.filter(req => req.status === 'pending_confirmation').length;
  const acceptedRequestsCount = filteredRequests.filter(req => req.status === 'accepted').length;
  const totalUnits = filteredRequests.reduce((sum, req) => sum + (req.unitsNeeded || req.units || 1), 0);

  // Get today's date for display
  const today = new Date().toISOString().split('T')[0];
  const isTodaySelected = selectedDate === today;

  return (
    <div className="space-y-8">
      {/* Hero Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-2xl shadow-sm border border-blue-100">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-indigo-500/5"></div>
        <div className="relative p-5">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                    Accepted Requests
                  </h1>
                  <p className="text-gray-600 text-sm mt-0.5">
                    Track and manage your accepted blood donation requests
                    {selectedDate && (
                      <span className="block mt-0.5 text-blue-600 font-medium">
                        {isTodaySelected ? 'Showing today\'s requests' :
                          `Showing requests from ${new Date(selectedDate).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}`}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-blue-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{totalRequests}</p>
                      <p className="text-xs text-gray-500">Total Requests</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-blue-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{acceptedRequestsCount}</p>
                      <p className="text-xs text-gray-500">Accepted</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-blue-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <ClockIcon className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{pendingConfirmation}</p>
                      <p className="text-xs text-gray-500">Pending</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-3 border border-blue-100 shadow-sm"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                      <Droplet className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-gray-900">{totalUnits}</p>
                      <p className="text-xs text-gray-500">Units Needed</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <Filter className="w-4 h-4 text-gray-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Filter Requests</h3>
              <p className="text-xs text-gray-500">Refine your accepted requests by status and date</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {/* Date Picker */}
            <div className="relative">
              <input
                type="date"
                value={selectedDate}
                onChange={handleDateChange}
                className="pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 cursor-pointer hover:bg-gray-100"
              />
              <Calendar className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>

            {/* Today Button */}
            <motion.button
              onClick={setToToday}
              className="px-3 py-2 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-all duration-200 flex items-center gap-1.5"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Calendar className="w-3.5 h-3.5" />
              Today
            </motion.button>

            {/* Clear Date Button */}
            {selectedDate && (
              <motion.button
                onClick={clearDateFilter}
                className="px-3 py-2 bg-gray-50 border border-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-100 transition-all duration-200 flex items-center gap-1.5"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <X className="w-3.5 h-3.5" />
                All Dates
              </motion.button>
            )}

            {/* Status Filter */}
            <div className="relative">
              <select
                className="pl-9 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 appearance-none cursor-pointer hover:bg-gray-100"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="">All Status</option>
                <option value="accepted">Accepted</option>
                <option value="pending_confirmation">Pending Confirmation</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
              <ClockIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Active Filters Display */}
        {(selectedDate || statusFilter) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 flex flex-wrap gap-2"
          >
            {selectedDate && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                {new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </span>
            )}
            {statusFilter && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                <Filter className="w-3 h-3" />
                {statusFilter === 'pending_confirmation' ? 'Pending Confirmation' :
                  statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </span>
            )}
          </motion.div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
            <Heart className="w-10 h-10 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading your accepted requests...</h3>
          <p className="text-gray-500">We're fetching your lifesaving commitments</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Oops! Something went wrong</h3>
          <p className="text-gray-500 mb-6">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchAcceptedRequests}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium"
          >
            Try Again
          </motion.button>
        </motion.div>
      )}

      {/* Requests Grid */}
      {!loading && !error && filteredRequests.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filteredRequests.map((request, index) => (
            <motion.div
              key={request.requestId || request.request || request._id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              className="group bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 hover:border-blue-300"
            >
              {/* Status Header */}
              <div className={`px-4 py-3 border-b border-gray-200 ${getStatusBadgeColor(request.status, request)}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/80 rounded-lg flex items-center justify-center shadow-sm">
                      {getStatusIcon(request.status, request)}
                    </div>
                    <div>
                      <span className={`text-sm font-bold ${getStatusBadgeColor(request.status, request)}`}>
                        {typeof getStatusDisplayText(request.status) === 'function'
                          ? getStatusDisplayText(request.status)(request)
                          : getStatusDisplayText(request.status)}
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        ID: {(request.requestId || request.request || request._id || '').toString().substring(0, 8)}
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/80 px-3 py-1 text-xs rounded-lg font-bold text-red-700 flex items-center gap-1.5 shadow-sm">
                    <Droplet className="w-3.5 h-3.5" />
                    {request.unitsNeeded || request.units || 1} {(request.unitsNeeded || request.units || 1) > 1 ? 'units' : 'unit'}
                  </div>
                </div>
              </div>

              <div className="p-5">
                {/* Main Content */}
                <div className="space-y-4">
                  {/* Blood Group & Patient */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Droplet className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900">
                        {request.bloodGroup || request.bloodType || 'Unknown'} Blood
                      </h3>
                      <div className="flex items-center gap-1.5 text-xs">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-gray-600 font-medium">
                          {request.requesterName || request.patientName || 'Requester'}
                        </span>
                        {(request.phone || request.contactPhone) && (
                          <motion.button
                            onClick={() => handleCallPatient(request.phone || request.contactPhone, request.requesterName || request.patientName)}
                            className="w-5 h-5 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title={`Call ${request.requesterName || request.patientName}`}
                          >
                            <PhoneCall className="w-2.5 h-2.5" />
                          </motion.button>
                        )}
                      </div>
                      {(request.phone || request.contactPhone) && (
                        <div className="flex items-center gap-1.5 text-xs mt-0.5">
                          <Phone className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-500">{request.phone || request.contactPhone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Key Details Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-blue-50 rounded-xl p-2.5 border border-blue-200">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Calendar className="w-3.5 h-3.5 text-blue-600" />
                        <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Created</span>
                      </div>
                      <p className="text-xs text-gray-800 font-medium">{formatDateWithTime(request.createdAt)}</p>
                    </div>

                    <div className="bg-green-50 rounded-xl p-2.5 border border-green-200">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Check className="w-3.5 h-3.5 text-green-600" />
                        <span className="text-[10px] font-semibold text-green-700 uppercase tracking-wide">Accepted</span>
                      </div>
                      <p className="text-xs text-gray-800 font-medium">{formatDateWithTime(request.acceptedDate)}</p>
                    </div>

                    <div className="bg-purple-50 rounded-xl p-2.5 border border-purple-200">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Calendar className="w-3.5 h-3.5 text-purple-600" />
                        <span className="text-[10px] font-semibold text-purple-700 uppercase tracking-wide">Donation</span>
                      </div>
                      <p className="text-xs text-gray-800 font-medium">
                        {request.donationDate ? formatDate(request.donationDate) : 'Not scheduled'}
                      </p>
                    </div>

                    <div className="bg-orange-50 rounded-xl p-2.5 border border-orange-200">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Hospital className="w-3.5 h-3.5 text-orange-600" />
                        <span className="text-[10px] font-semibold text-orange-700 uppercase tracking-wide">Hospital</span>
                      </div>
                      <p className="text-xs text-gray-800 font-medium truncate" title={request.hospitalName || 'Unknown Hospital'}>
                        {request.hospitalName || 'Unknown Hospital'}
                      </p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs">
                      <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600 truncate" title={request.address || 'Unknown Address'}>
                        {request.address || 'Unknown Address'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs">
                      <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-600 flex-1">{request.phone || request.contactPhone || 'Unknown Phone'}</span>
                      {(request.phone || request.contactPhone) && (
                        <motion.button
                          onClick={() => handleCallPatient(request.phone || request.contactPhone, request.requesterName || request.patientName)}
                          className="w-6 h-6 bg-green-500 hover:bg-green-600 text-white rounded-md flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          title={`Call ${request.requesterName || request.patientName}`}
                        >
                          <PhoneCall className="w-3 h-3" />
                        </motion.button>
                      )}
                    </div>
                    {request.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-600 truncate" title={request.location}>
                          {request.location}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Status-specific Information */}
                  {request.status === 'pending_confirmation' && (
                    <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-2xl border border-yellow-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-yellow-100 rounded-xl flex items-center justify-center">
                          <ClockIcon className="w-4 h-4 text-yellow-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-yellow-800 text-sm">Awaiting Verification</h4>
                          <p className="text-xs text-yellow-700">Your donation proof is being reviewed</p>
                        </div>
                      </div>
                      {request.donationProofPhoto && (
                        <div className="mt-3 flex items-center gap-2">
                          <img
                            src={request.donationProofPhoto}
                            alt="Donation proof"
                            className="w-12 h-12 object-cover rounded-lg border-2 border-yellow-200"
                          />
                          <span className="text-xs text-yellow-600">Proof submitted</span>
                        </div>
                      )}
                    </div>
                  )}

                  {request.status === 'donated' && (
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-xl flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-800 text-sm">Donation Confirmed</h4>
                          <p className="text-xs text-green-700">Verified by requester</p>
                        </div>
                      </div>
                      {request.requesterFeedback && (
                        <div className="mt-2 bg-white/50 p-2 rounded-lg">
                          <p className="text-xs text-gray-700 italic">"{request.requesterFeedback}"</p>
                        </div>
                      )}
                    </div>
                  )}

                  {request.status === 'rejected' && (
                    <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-2xl border border-red-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-red-100 rounded-xl flex items-center justify-center">
                          <X className="w-4 h-4 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-red-800 text-sm">Not Verified</h4>
                          <p className="text-xs text-red-700">Requester couldn't verify donation</p>
                        </div>
                      </div>
                      {request.requesterFeedback && (
                        <div className="mt-2 bg-white/50 p-2 rounded-lg">
                          <p className="text-xs text-gray-700 italic">"{request.requesterFeedback}"</p>
                        </div>
                      )}
                    </div>
                  )}

                  {request.status === 'pending' && request.needsReupload && (
                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-2xl border border-orange-200">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-xl flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-orange-800 text-sm">Reupload Required</h4>
                          <p className="text-xs text-orange-700">Please upload clearer proof</p>
                        </div>
                      </div>
                      {request.requesterFeedback && (
                        <div className="mt-2 bg-white/50 p-2 rounded-lg">
                          <p className="text-xs text-gray-700 italic">"{request.requesterFeedback}"</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    {request.status !== 'donated' && request.status !== 'Donated' && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="flex-1 px-4 py-2 border-2 border-gray-200 text-gray-700 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium"
                        onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(request.address || 'Unknown Address')}`, '_blank')}
                      >
                        <MapPin className="w-4 h-4" />
                        Directions
                      </motion.button>
                    )}

                    {(request.status === 'accepted' || (request.status === 'pending' && request.needsReupload)) && (
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => openConfirmationModal(request)}
                        className={`flex-1 px-4 py-2 ${request.needsReupload ? 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700' : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'} text-white rounded-xl transition-all duration-200 flex items-center justify-center gap-2 text-sm font-medium shadow-lg hover:shadow-xl`}
                      >
                        <Camera className="w-4 h-4" />
                        {request.needsReupload ? 'Reupload' : 'Confirm'}
                      </motion.button>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredRequests.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center"
        >
          <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">No Accepted Requests Found</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {selectedDate && statusFilter
              ? `You don't have any ${statusFilter} requests from ${new Date(selectedDate).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}.`
              : selectedDate
                ? `You don't have any requests from ${new Date(selectedDate).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}.`
                : statusFilter
                  ? `You don't have any requests with "${statusFilter}" status.`
                  : "You haven't accepted any blood donation requests yet. Start your lifesaving journey by accepting requests!"}
          </p>
          {(statusFilter || selectedDate) && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                setStatusFilter('');
                setSelectedDate('');
              }}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Clear All Filters
            </motion.button>
          )}
        </motion.div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedRequest.needsReupload ? 'Reupload Donation Proof' : 'Confirm Blood Donation'}
                </h3>
                <button
                  onClick={closeConfirmationModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-8">
                {/* Contact Information */}
                {(selectedRequest.phone || selectedRequest.contactPhone) && (
                  <div className="mb-6 p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-blue-800 mb-1">Patient Contact Information</p>
                        <p className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">Name:</span> {selectedRequest.requesterName || selectedRequest.patientName}
                        </p>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">Phone:</span> {selectedRequest.phone || selectedRequest.contactPhone}
                        </p>
                      </div>
                      <motion.button
                        onClick={() => handleCallPatient(selectedRequest.phone || selectedRequest.contactPhone, selectedRequest.requesterName || selectedRequest.patientName)}
                        className="w-10 h-10 bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        title={`Call ${selectedRequest.requesterName || selectedRequest.patientName}`}
                      >
                        <PhoneCall className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                )}

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {selectedRequest.needsReupload
                    ? 'The requester has asked for a clearer or different proof of your donation. Please upload a new photo.'
                    : 'Please upload a photo as proof of your donation (e.g., donation certificate, bandage on arm, or hospital receipt).'}
                </p>

                {selectedRequest.needsReupload && selectedRequest.requesterFeedback && (
                  <div className="mb-6 p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200">
                    <p className="text-sm font-semibold text-orange-800 mb-2">Requester Feedback:</p>
                    <p className="text-sm text-gray-700 italic">"{selectedRequest.requesterFeedback}"</p>
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Donation Photo*
                  </label>

                  {donationPhoto ? (
                    <div className="relative">
                      <img
                        src={donationPhoto.preview}
                        alt="Donation proof"
                        className="w-full h-48 object-cover rounded-xl border-2 border-green-200"
                      />
                      <button
                        onClick={() => {
                          setDonationPhoto(null);
                          toast.info('Photo removed. Please upload a new one.');
                        }}
                        className="absolute top-3 right-3 bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="mt-3 bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700 flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Photo uploaded successfully! Click "Confirm Donation" to submit.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => document.getElementById('donation-photo-input').click()}
                    >
                      <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-400 mt-2">PNG, JPG, JPEG (max 5MB)</p>
                    </div>
                  )}

                  <input
                    type="file"
                    id="donation-photo-input"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoChange}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Additional Notes (Optional)
                  </label>
                  <textarea
                    value={donationNotes}
                    onChange={(e) => setDonationNotes(e.target.value)}
                    placeholder="Add any additional information about your donation..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={closeConfirmationModal}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                  disabled={submittingConfirmation || uploadingPhoto}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    submitDonationConfirmation();
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 flex items-center justify-center gap-3 font-medium shadow-lg"
                  disabled={!donationPhoto || submittingConfirmation || uploadingPhoto}
                >
                  {(submittingConfirmation || uploadingPhoto) ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                      {uploadingPhoto ? 'Uploading...' : 'Confirming...'}
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Confirm Donation
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AcceptedRequests;